// Список питань з фільтрами + пагінацією. Усі фільтри і сторінка — у URL,
// щоб після збереження питання повернутись з тим самим скролом/фільтром.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, LICENSE_CATEGORIES, type AdminCategory, type LicenseCategory } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

const PAGE_SIZE = 50;

type IsActiveFilter = 'all' | 'true' | 'false';
type LicenseScope = 'all' | 'specific' | 'universal';

export function AdminQuestions() {
  // Усі фільтри читаємо/пишемо в URL — тоді після /admin/questions/:id → "Зберегти" → navigate(-1)
  // ми повертаємось на ту саму сторінку зі збереженими фільтрами.
  const [params, setParams] = useSearchParams();

  const categoryId = params.get('categoryId') ? Number(params.get('categoryId')) : undefined;
  const isActive = (params.get('isActive') as IsActiveFilter | null) ?? 'all';
  const licenseCategory = (params.get('licenseCategory') as LicenseCategory | null) ?? undefined;
  const licenseScope = (params.get('licenseScope') as LicenseScope | null) ?? 'all';
  const page = params.get('page') ? Math.max(0, Number(params.get('page')) - 1) : 0;
  const searchFromUrl = params.get('search') ?? '';

  // Search debounce — у URL-стейт пишемо тільки коли юзер припинив друкувати
  const [searchInput, setSearchInput] = useState(searchFromUrl);
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (searchInput) next.set('search', searchInput);
      else next.delete('search');
      // Скидаємо сторінку при зміні пошуку
      if ((params.get('search') ?? '') !== searchInput) next.delete('page');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function update(patch: Record<string, string | undefined>, resetPage = true) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.delete('page');
    setParams(next, { replace: false });
  }

  const cats = useFetch<AdminCategory[]>(() => api.admin.listCategories(), []);
  const list = useFetch(
    () =>
      api.admin.listQuestions({
        categoryId,
        isActive,
        search: searchFromUrl || undefined,
        licenseCategory,
        licenseScope,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    [categoryId, isActive, searchFromUrl, licenseCategory, licenseScope, page],
  );

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;
  const linkSuffix = params.toString() ? `?${params.toString()}` : '';

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap gap-2">
        <select
          value={categoryId ?? ''}
          onChange={(e) => update({ categoryId: e.target.value || undefined })}
          className="rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm"
        >
          <option value="">Всі категорії</option>
          {cats.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nameUk}
            </option>
          ))}
        </select>
        <select
          value={isActive}
          onChange={(e) => update({ isActive: e.target.value === 'all' ? undefined : e.target.value })}
          className="rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm"
        >
          <option value="all">Усі</option>
          <option value="true">Активні</option>
          <option value="false">Неактивні</option>
        </select>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Пошук за текстом…"
          className="min-w-0 flex-1 rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm placeholder:text-tg-hint"
        />
        <Link to="/admin/questions/new" className="btn-primary">
          + Створити
        </Link>
      </div>

      {/* Швидка розмітка для поточних фільтрів — переходимо з тим же ?categoryId=… */}
      <Link
        to={`/admin/questions/mark${categoryId ? `?categoryId=${categoryId}` : ''}`}
        className="btn-primary block w-full text-center"
      >
        ⚡ Швидка розмітка правильних відповідей{categoryId ? ' у цій категорії' : ' (усі категорії)'}
      </Link>

      {/* Фільтри за категорією прав */}
      <div className="flex flex-wrap gap-2">
        <select
          value={licenseScope}
          onChange={(e) => update({ licenseScope: e.target.value === 'all' ? undefined : e.target.value })}
          className="rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm"
        >
          <option value="all">Будь-яка область прав</option>
          <option value="universal">Тільки універсальні (для всіх)</option>
          <option value="specific">Тільки специфічні</option>
        </select>
        <select
          value={licenseCategory ?? ''}
          onChange={(e) => update({ licenseCategory: e.target.value || undefined })}
          className="rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm"
        >
          <option value="">— Без фільтру за категорією прав —</option>
          {LICENSE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              Підходить для {c}
            </option>
          ))}
        </select>
      </div>

      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <div className="flex items-center justify-between text-xs text-tg-hint">
            <span>
              Знайдено: <strong>{list.data.total}</strong>
            </span>
            {totalPages > 1 && (
              <span>
                Сторінка {page + 1} з {totalPages}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {list.data.items.length === 0 && (
              <p className="card text-sm text-tg-hint">Питань немає. Створи перше — кнопка вгорі.</p>
            )}
            {list.data.items.map((q) => (
              <Link
                key={q.id}
                // Передаємо поточні фільтри в URL — після save форма поверне нас сюди
                to={`/admin/questions/${q.id}${linkSuffix}`}
                className="card flex items-start gap-3 hover:bg-tg-bg/40"
              >
                {q.imageUrl ? (
                  <img src={q.imageUrl} alt="" className="h-14 w-14 rounded object-cover" loading="lazy" />
                ) : (
                  <div className="grid h-14 w-14 place-items-center rounded bg-tg-bg text-xs text-tg-hint">
                    Без фото
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-sm">
                    {q.order > 0 && <span className="font-semibold text-tg-link">{q.order}.</span>}{' '}
                    {q.textUk}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-tg-hint">
                    <span>{q.category.nameUk}</span>
                    <span>·</span>
                    <span>складність {q.difficulty}</span>
                    {q.licenseCategories.length > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-tg-link">{q.licenseCategories.join(', ')}</span>
                      </>
                    )}
                    {!q.isActive && (
                      <>
                        <span>·</span>
                        <span className="text-tg-destructive">неактивне</span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Пагінація — показуємо тільки якщо більше однієї сторінки */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => update({ page: page > 1 ? String(page) : undefined }, false)}
                disabled={page === 0 || list.loading}
                className="btn-secondary flex-1 disabled:opacity-40"
              >
                ← Попередня
              </button>
              <select
                value={page}
                onChange={(e) =>
                  update({ page: Number(e.target.value) > 0 ? String(Number(e.target.value) + 1) : undefined }, false)
                }
                className="rounded-lg bg-tg-secondary-bg px-3 py-3 text-sm"
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <option key={i} value={i}>
                    {i + 1}/{totalPages}
                  </option>
                ))}
              </select>
              <button
                onClick={() =>
                  update({ page: String(Math.min(totalPages, page + 2)) }, false)
                }
                disabled={page >= totalPages - 1 || list.loading}
                className="btn-secondary flex-1 disabled:opacity-40"
              >
                Наступна →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
